import { Fragment, type ComponentType } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { createApi, ApiError, type Api } from './api.js';
import { freshToken, loadSession, parseCallback, saveSession, sessionExpired, takeReturnTo, tokenEmail } from './auth.js';
import { movedPath, navigate, parseRoute, type Route } from './router.js';
import { Loading, Toaster, toast } from './ui.js';
import { lazyPage, preloadAll, type LazyPage } from './lazy.js';
import { AvailabilityOverview } from './pages/AvailabilityOverview.js';
import { FreelancerAvailability } from './pages/FreelancerAvailability.js';
import { CrewPage, type CrewReviewOpen } from './pages/Crew.js';
import { ShootPrep } from './pages/shootPrep.js';
import { Home } from './pages/Home.js';
import { Login, type LoginNotice } from './pages/Login.js';
import { LegalPage } from './pages/Legal.js';
import { SetPasswordPage } from './pages/PasswordSignin.js';
import { NotAvailable } from './pages/NotAvailable.js';
const SettingsEmail = lazyPage(() => import('./pages/Settings.js').then((m) => m.SettingsEmail));
const SettingsModules = lazyPage(() => import('./pages/Settings.js').then((m) => m.SettingsModules));
const SettingsWorkload = lazyPage(() => import('./pages/Settings.js').then((m) => m.SettingsWorkload));
import { MySettings } from './pages/MySettings.js';
import { PersonDetail, Team } from './pages/Team.js';
import { NewsComposer, NewsFeed, WhatsNew } from './pages/News.js';
import { loadReleases } from './releases.js';
import { Changelog } from './pages/Changelog.js';
import { PageTour, playTour, TOUR_TEXT } from './tour.js';
import type { TourId } from './tours.js';
const InvitePerson = lazyPage(() => import('./pages/People.js').then((m) => m.InvitePerson));
const PeopleList = lazyPage(() => import('./pages/People.js').then((m) => m.PeopleList));
const PostProduction = lazyPage(() => import('./pages/PostProduction.js').then((m) => m.PostProduction));
const PersonAdmin = lazyPage(() => import('./pages/People.js').then((m) => m.PersonAdmin));
import { ProjectPage, ProjectsList } from './pages/Projects.js';
import { SideBySide } from './pages/sideBySide.js';
import { PANEL_TEXT, PanelKeyDialog, StaffReview } from './pages/Review.js';
import { ReleaseDocument, SignPage } from './pages/Actors.js';
const ESignPage = lazyPage(() => import('./pages/ESign.js').then((m) => m.ESignPage));
const ESignVerifyPage = lazyPage(() => import('./pages/ESign.js').then((m) => m.ESignVerifyPage));
const ApplyPage = lazyPage(() => import('./pages/Talent.js').then((m) => m.ApplyPage));
const TalentLibrary = lazyPage(() => import('./pages/Talent.js').then((m) => m.TalentLibrary));
import { CastingAnswerPage } from './pages/Casting.js';
import { ClientCard } from './pages/Notes.js';
import { ClientsList } from './pages/Clients.js';
const ContactsList = lazyPage(() => import('./pages/Contacts.js').then((m) => m.ContactsList));
const Portal = lazyPage(() => import('./pages/Portal.js').then((m) => m.Portal));
const PortalSignin = lazyPage<{ crew?: boolean }>(() => import('./pages/Portal.js').then((m) => m.PortalSignin as ComponentType<{ crew?: boolean }>));
const AgreementTemplatesPage = lazyPage(() => import('./pages/AgreementTemplates.js').then((m) => m.AgreementTemplatesPage));
const AgreementDocument = lazyPage(() => import('./pages/Agreements.js').then((m) => m.AgreementDocument));
const AgreementEditor = lazyPage(() => import('./pages/Agreements.js').then((m) => m.AgreementEditor));
const ContractDocument = lazyPage(() => import('./pages/Agreements.js').then((m) => m.ContractDocument));
const AgreementsList = lazyPage(() => import('./pages/Agreements.js').then((m) => m.AgreementsList));
const PriceListPage = lazyPage(() => import('./pages/Agreements.js').then((m) => m.PriceListPage));
const DealsBoard = lazyPage(() => import('./pages/Deals.js').then((m) => m.DealsBoard));
import { ContractsPage } from './pages/Contracts.js';
const FinancePage = lazyPage(() => import('./pages/Finance.js').then((m) => m.FinancePage));
import { WORKSPACE_DOMAIN } from './config.js';
const MyTime = lazyPage(() => import('./pages/MyTime.js').then((m) => m.MyTime));
import { openTimer, TimerPill } from './timer.js';
import { PlanHost } from './planTime.js';
import { KlaraLauncher, openKlara } from './klara.js';
const Tickets = lazyPage(() => import('./pages/Tickets.js').then((m) => m.Tickets));
import { Notifications } from './notify.js';
import { GettingStarted, guideWork } from './guide.js';
import { ArchivePage } from './pages/Archive.js';
import { isCrewView } from './pages/Today.js';
import { Shell, TabsContext, type Command, type NavItem, type Tab } from './shell.js';
import { allowViewAs, clearViewAs, loadViewAs, ViewAsBar, ViewAsDialog, viewAsText, type ViewAsState } from './viewAs.js';
import { EmergencyLinks, keepEmergency, type EmergencyLink } from './emergency.js';
import { DICT, LangContext, rememberLang, storedLang, useLang, useT, type Lang } from './i18n.js';

/** The pages downloaded when first needed (lazy.tsx); fetched in the background once a signed-in page is up. */
const STAFF_PAGES = [SettingsEmail, InvitePerson, ESignPage, TalentLibrary, ContactsList, AgreementTemplatesPage, AgreementsList, DealsBoard, FinancePage, MyTime, Tickets, PostProduction] as LazyPage<never>[];

export interface Me {
  person: { id: string; name: string; kind: 'employee' | 'freelancer'; language?: Lang; newsPopup?: boolean };
  roles: string[];
  modules: string[];
  caps: string[];
  via: 'staff' | 'link';
  /** set while an admin looks as this person or role ("Visa som") */
  viewAs?: { mode: 'person' | 'role'; name: string; by: { id: string; name: string }; expiresAt: string };
  /** links to this person's emergency copies in Google Drive, kept on the device for when the Hub is down */
  emergencyCopies?: EmergencyLink[];
}

/** Kamera alone on a project opens it on Shoots, where their work is (owner 2026-10-21). */
const cameraOnly = (keys: string[] | undefined) => !!keys?.length && keys.every((k) => k === 'camera');
const todayStockholm = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Stockholm' }).format(new Date());
/** The route for the current address; an old address that moved is swapped for the new one first. */
const current = (): Route => {
  const moved = movedPath(location.pathname, location.search);
  if (moved) history.replaceState(null, '', moved);
  return parseRoute(location.pathname, location.search);
};
const useRoute = () => {
  const [r, setR] = useState<Route>(current);
  useEffect(() => {
    const on = () => setR(current());
    addEventListener('popstate', on);
    return () => removeEventListener('popstate', on);
  }, []);
  return r;
};

const LINK_LANG = 'kff.link.lang';
const LINK_TX = {
  sv: { bad: 'Länken fungerar inte', badBody: 'Länken fungerar inte – be din producent på KFF om en ny länk.', email: 'Logga in med e-post' },
  en: { bad: "The link doesn't work", badBody: "The link doesn't work – ask your producer at KFF for a new one.", email: 'Sign in with email' }
};
const linkLang = (): Lang => {
  try {
    return localStorage.getItem(LINK_LANG) === 'en' ? 'en' : 'sv';
  } catch {
    return 'sv';
  }
};
/** A freelancer's personal link or email-code session, kept on the device so the Hub's addresses work like an employee's. */
const LINK_KEY = 'kff.link';
const loadLink = (): string | null => {
  try {
    return localStorage.getItem(LINK_KEY);
  } catch {
    return null;
  }
};
const saveLink = (token: string | null) => {
  try {
    if (token) localStorage.setItem(LINK_KEY, token);
    else localStorage.removeItem(LINK_KEY);
  } catch {
    /* private mode: the session lasts until the page closes */
  }
};
/** The old link page's tabs (#projects …) open the same page in the Hub. */
const LINK_TAB_PATH: Record<string, string> = { projects: '/projects', availability: '/team/availability', news: '/news' };

/**
 * A freelancer signed in with their personal link or an email code: the same Hub, menu and pages as an employee with the
 * same role (owner 2026-10-20: freelancer is the form of collaboration, not an access level). Swedish unless they pick
 * English (remembered on the device; the API answers in the same language, see api.ts).
 */
function LinkApp(p: { token: string; route: Route; signOut: () => void }) {
  const [lang, setLang] = useState<Lang>(linkLang);
  const [rev, setRev] = useState(0);
  const api = useMemo(() => {
    // the API reads the language from the page before the first call
    document.documentElement.lang = linkLang();
    return createApi({ kind: 'link', token: p.token });
  }, [p.token]);
  const [me, setMe] = useState<Me | null>(null);
  const [err, setErr] = useState<string>('');
  useEffect(() => {
    api.get<Me>('/api/me').then(
      (m) => {
        setMe(m);
        preloadAll(STAFF_PAGES);
        keepEmergency(m.emergencyCopies);
      },
      (e) => setErr(e instanceof ApiError && e.status === 401 ? LINK_TX[linkLang()].badBody : e instanceof Error ? e.message : String(e))
    );
  }, [api]);
  const pick = (l: Lang) => {
    try {
      localStorage.setItem(LINK_LANG, l);
    } catch {
      /* private mode: the switch lasts until the page closes */
    }
    document.documentElement.lang = l;
    setLang(l);
    setRev((r) => r + 1);
  };
  if (err) return <LinkBroken lang={lang} err={err} />;
  if (!me) return <Loading label={DICT[lang].common.loading} />;
  return (
    <LangContext.Provider value={lang}>
      <StaffApp route={p.route} api={api} me={me} signOut={p.signOut} onLang={pick} rev={rev} viewAs={null} />
    </LangContext.Provider>
  );
}

function LinkBroken(p: { lang: Lang; err: string }) {
  const lt = LINK_TX[p.lang];
  return (
    <LangContext.Provider value={p.lang}>
      <div class="shell shell--link" lang={p.lang}>
        <main id="main">
          <div class="screen screen--narrow">
            <h1 class="h1">{lt.bad}</h1>
            <p>{p.err}</p>
            {p.err !== lt.badBody ? <EmergencyLinks lang={p.lang} /> : null}
            <p>
              <a class="btn" href="/t/signin">
                {lt.email}
              </a>
            </p>
          </div>
        </main>
      </div>
    </LangContext.Provider>
  );
}

function StaffApp(p: { route: Route; api: Api; me: Me; signOut: () => void; onLang: (l: Lang) => void; rev: number; viewAs: ViewAsState | null }) {
  const { route, api, me } = p;
  // "New for you": the "New" dots by new buttons (releases.tsx), read once
  useEffect(() => void loadReleases(api), [api]);
  const [viewAsOpen, setViewAsOpen] = useState(false);
  const canViewAs = me.caps.includes('people.view_as') && !me.viewAs;
  allowViewAs(canViewAs);
  const t = useT();
  const lang = useLang();
  const n = t.nav;
  const has = (m: string) => me.modules.includes(m);
  const [panelKey, setPanelKey] = useState(false);
  const admin = me.caps.includes('modules.admin');
  // "Kom igång" for the people who edit and film (the API decides the same way, services/onboarding.ts)
  const guideOn = me.person.kind === 'freelancer' || me.roles.some((r) => r === 'editor' || r === 'camera');
  // editors and camera: their own "Mitt arbete" (the day's digest) and the projects as rows (owner 2026-10-20)
  const crew = isCrewView(me.roles) && has('projects');
  // a freelancer on their link or email code: the same menu, with the crew's own project and review pages (thin, read only)
  const link = me.via === 'link';
  const peopleOn = me.caps.includes('people.admin') || (me.caps.includes('people.view') && has('team'));
  // a freelancer's own availability is a tab under My work (owner 2026-10-20); employees' comes from Personio
  const ownAvailability = me.person.kind === 'freelancer' && has('availability') && !me.caps.includes('availability.view');
  const myWork = route.name === 'home' || route.name.startsWith('news') || route.name === 'my-time' || (ownAvailability && route.name === 'availability');
  // freelancers log time too (owner 2026-10-20)
  const timeOn = has('projects');
  // the film archive is internal: employees only (the API says the same)
  const archiveOn = has('archive') && me.via === 'staff' && me.person.kind === 'employee';
  // Post production for Redigering and Kamera (owner 2026-11-03): the guide and the tools are modules of their own
  const postGuideOn = has('post_guide');
  const postToolsOn = has('post_tools');
  const postAccountsOn = has('post_accounts');
  const postTipsOn = has('post_tips');
  // one overview page, no tabs (owner 2026-11-03: "mer likt bilden … en hel dashboard med överblick")
  const postOn = postTipsOn || postAccountsOn || postToolsOn || postGuideOn;
  const quotesOn = has('clients') && me.caps.includes('prices.view');
  const financeOn = me.via === 'staff' && me.caps.includes('finance.view');
  // producers attest supplier invoices on their projects there (owner 2026-10-29); the rest of Finance is admin only
  const costsOn = financeOn || (me.via === 'staff' && me.caps.includes('invoicing.confirm'));
  const quotesRoute = route.name === 'agreements' || route.name === 'deals' || route.name === 'agreement' || route.name === 'price-list' || route.name === 'agreement-templates';
  // Team holds three tabs: Capacity (the week grid), Availability (freelancers' weeks) and People
  const availabilityOn = has('availability') && me.caps.includes('availability.view');
  const teamRoute = route.name === 'team' || route.name === 'person' || route.name === 'availability' || route.name.startsWith('people');
  const teamTabs: Tab[] = [
    ...(has('team') ? [{ path: '/team', label: n.capacity, active: route.name === 'team' || route.name === 'person' }] : []),
    ...(availabilityOn ? [{ path: '/team/availability', label: n.availability, active: route.name === 'availability' }] : []),
    ...(peopleOn ? [{ path: '/team/people', label: admin ? n.peopleAccess : n.people, active: route.name.startsWith('people') }] : [])
  ];
  const reviewPath = (o: CrewReviewOpen) => `/projects/${o.projectId}/review/${o.videoId}${o.version ? `?version=${encodeURIComponent(o.version)}` : ''}`;
  // grouped under Production / Business / Company; the shell shows the headings only when the menu is long enough,
  // and Tickets and Settings sit in the foot (owner 2026-10-27)
  const nav: (NavItem & { on: boolean })[] = [
    // the same menu name for everyone; what the page holds follows the role (owner 2026-10-20)
    { path: '/', label: n.myWork, icon: 'inbox', tab: true, on: true, active: myWork },
    { path: '/projects', label: n.projects, icon: 'folder', group: n.groupProduction, tab: true, on: has('projects'), active: route.name.startsWith('project') || route.name === 'side' },
    { path: '/post', label: n.post, icon: 'scissors', group: n.groupProduction, on: postOn, active: route.name === 'post' },
    { path: '/actors', label: n.actors, icon: 'star', group: n.groupProduction, on: has('projects') && me.caps.includes('projects.edit') && me.via === 'staff', active: route.name === 'talent' },
    // editing and camera work from the project, not the client register (owner 2026-10-21)
    { path: '/clients', label: n.clients, icon: 'briefcase', group: n.groupBusiness, tab: true, on: has('projects') && me.person.kind === 'employee' && !crew, active: route.name === 'client' || route.name === 'clients' },
    { path: '/agreements/board', label: n.sales, icon: 'trending-up', group: n.groupBusiness, on: quotesOn, active: quotesRoute },
    // signed agreements on their own, read only, for producers too; selling under Sales (owner 2026-10-27)
    { path: '/contracts', label: n.contracts, icon: 'file-text', group: n.groupBusiness, on: quotesOn, active: route.name === 'contracts' },
    // Bokio's invoices, the month's invoice bases and the customer link; admins only (owner 2026-10-29)
    { path: financeOn ? '/finance' : '/finance/costs', label: n.finance, icon: 'wallet', group: n.groupBusiness, on: costsOn, active: route.name === 'finance' },
    { path: teamTabs[0]?.path ?? '/team', label: n.team, icon: 'users', group: n.groupCompany, tab: true, on: teamTabs.length > 0, active: teamRoute },
    { path: '/archive', label: n.archive, icon: 'film', group: n.groupCompany, on: archiveOn, active: route.name === 'archive' },
    { path: '/tickets', label: n.tickets, icon: 'ticket', foot: true, on: has('klara'), active: route.name === 'tickets' },
    // every role has its own settings (owner 2026-10-20); admins also get the Hub's under the same item.
    // It sits first in the account menu, top right, not in the side menu (owner 2026-10-28)
    { path: '/settings', label: n.settings, icon: 'settings', account: true, on: true, active: route.name.startsWith('settings') }
  ].filter((n) => n.on);
  const tabs: { label: string; tabs: Tab[]; more?: string } | null =
    myWork && (has('news') || timeOn || ownAvailability)
      ? {
          label: n.myWork,
          tabs: [
            { path: '/', label: n.now, active: route.name === 'home' },
            ...(has('news') ? [{ path: '/news', label: n.news, active: route.name.startsWith('news') }] : []),
            ...(timeOn ? [{ path: '/my-time', label: n.myTime, active: route.name === 'my-time' }] : []),
            ...(ownAvailability ? [{ path: '/team/availability', label: n.availability, active: route.name === 'availability' }] : [])
          ]
        }
      : teamRoute && teamTabs.length > 1
        ? { label: n.team, tabs: teamTabs }
        : route.name === 'finance' && financeOn
        ? {
            label: n.finance,
            tabs: [
              { path: '/finance', label: n.financeOverview, active: route.tab === 'overview' },
              { path: '/finance/invoicing', label: n.toInvoice, active: route.tab === 'invoicing' },
              { path: '/finance/costs', label: n.costs, active: route.tab === 'costs' },
              { path: '/finance/margins', label: n.margins, active: route.tab === 'margins' },
              { path: '/finance/terms', label: n.agreementTerms, active: route.tab === 'terms' },
              { path: '/finance/customers', label: n.bokioCustomers, active: route.tab === 'customers' }
            ]
          }
        : route.name === 'clients'
        ? {
            // companies and their people under one menu row (owner 2026-10-27)
            label: n.clients,
            tabs: [
              { path: '/clients', label: n.companies, active: route.tab === 'companies' },
              { path: '/clients/contacts', label: n.contacts, active: route.tab === 'contacts' }
            ]
          }
        : route.name === 'talent'
        ? {
            label: n.actors,
            tabs: [
              { path: '/actors', label: n.library, active: route.tab === 'library' },
              { path: '/actors/applications', label: n.applications, active: route.tab === 'applications' }
            ]
          }
        : route.name === 'project'
        ? {
            // the same tabs on the personal link: freelancers get the editor/camera profile (owner 2026-10-20)
            label: n.project,
            more: n.moreTabs,
            tabs: [
              { path: `/projects/${route.id}`, label: n.videos, active: route.tab === 'videos' },
              { path: `/projects/${route.id}/brief`, label: n.brief, active: route.tab === 'brief' },
              { path: `/projects/${route.id}/shoots`, label: n.shoots, active: route.tab === 'shoots' },
              // the crew see who is in the films and whether their model release is signed (owner 2026-10-27); producers
              // cast and sign there, so it is a tab of its own for them, not under More
              { path: `/projects/${route.id}/actors`, label: n.actors, active: route.tab === 'actors', more: !me.caps.includes('projects.edit') },
              { path: `/projects/${route.id}/notes`, label: n.notes, active: route.tab === 'notes' },
              { path: `/projects/${route.id}/files`, label: n.files, active: route.tab === 'files' },
              { path: `/projects/${route.id}/activity`, label: n.activity, active: route.tab === 'activity', more: true },
              { path: `/projects/${route.id}/details`, label: n.details, active: route.tab === 'details', more: true }
            ]
          }
      : quotesOn && (route.name === 'agreements' || route.name === 'deals' || route.name === 'price-list' || route.name === 'agreement-templates')
        ? {
            label: n.sales,
            tabs: [
              { path: '/agreements/board', label: n.deals, active: route.name === 'deals' },
              { path: '/agreements', label: n.quotes, active: route.name === 'agreements' },
              { path: '/agreements/price-list', label: n.priceList, active: route.name === 'price-list' },
              { path: '/agreements/templates', label: n.agreementTemplates, active: route.name === 'agreement-templates' }
            ]
          }
      : route.name.startsWith('settings')
        ? {
            label: n.settings,
            // everyone: My settings and What's new (the changelog, owner 2026-10-31); admins also the Hub's settings
            tabs: [
              { path: '/settings', label: n.mySettings, active: route.name === 'settings-me' },
              ...(admin
                ? [
                    { path: '/settings/modules', label: n.modules, active: route.name === 'settings-modules' },
                    { path: '/settings/connections', label: n.connections, active: route.name === 'settings-connections' },
                    { path: '/settings/email', label: n.email, active: route.name === 'settings-email' },
                    { path: '/settings/workload', label: n.workloadSource, active: route.name === 'settings-workload' }
                  ]
                : []),
              { path: '/settings/whats-new', label: n.whatsNew, active: route.name === 'settings-whats-new' }
            ]
          }
        : null;
  const [startNews] = useState(() => !route.name.startsWith('news') && route.name !== 'settings-whats-new' && route.name !== 'actor-release' && route.name !== 'agreement-doc');
  const go = (path: string) => () => navigate(path);
  const commands = useMemo<Command[]>(
    () => [
      ...nav.map((n) => ({ group: t.cmd.goTo, label: n.label, icon: n.icon, run: go(n.path) })),
      ...(has('news') ? [{ group: t.cmd.goTo, label: n.news, icon: 'file-text', hint: n.myWork, run: go('/news') }] : []),
      ...(admin ? [{ group: t.cmd.goTo, label: n.workloadSource, icon: 'settings', hint: n.settings, run: go('/settings/workload') }] : []),
      ...(has('news') && me.caps.includes('news.post') ? [{ group: t.cmd.actions, label: t.cmd.writeNews, icon: 'file-text', run: go('/news/new') }] : []),
      ...(admin ? [{ group: t.cmd.actions, label: t.cmd.invitePerson, icon: 'user-plus', run: go('/people/invite') }] : []),
      ...teamTabs.slice(1).map((x) => ({ group: t.cmd.goTo, label: x.label, icon: 'users', hint: n.team, run: go(x.path) })),
      ...(timeOn
        ? [
            { group: t.cmd.actions, label: t.cmd.startTimer, icon: 'clock', hint: t.cmd.time, run: () => openTimer({ mode: 'start' }) },
            { group: t.cmd.actions, label: t.cmd.logTime, icon: 'clock', hint: t.cmd.logTimeHint, run: () => openTimer({ mode: 'add' }) },
            { group: t.cmd.goTo, label: n.myTime, icon: 'clock', hint: n.myWork, run: go('/my-time') }
          ]
        : []),
      { group: t.cmd.actions, label: TOUR_TEXT[lang].play, icon: 'help', hint: TOUR_TEXT[lang].playHint, run: () => void (playTour() || toast({ text: TOUR_TEXT[lang].none })) },
      ...(has('klara') ? [{ group: t.cmd.actions, label: t.cmd.askKlara, icon: 'message', hint: t.cmd.askKlaraHint, run: openKlara }] : []),
      ...(guideOn ? [{ group: t.cmd.goTo, label: t.shell.guide, icon: 'star', run: go('/guide') }] : []),
      ...(canViewAs ? [{ group: t.cmd.actions, label: viewAsText(lang).title, icon: 'eye', run: () => setViewAsOpen(true) }] : [])
    ],
    [me, nav.length, lang]
  );
  const openNews = (id: string | null) => navigate(id ? `/news/${id}` : '/news');
  const noAccess = (title: string, who: string) => <NotAvailable title={title} heading={t.gate.notForRole} message={t.gate.forWho(title, who)} />;
  const blocked = (title: string) => <NotAvailable title={title} message={t.gate.blocked(title)} />;
  const sellers = t.gate.sellers;
  // documents print without the Hub around them, but still sit in a main landmark
  if (route.name === 'actor-release' && has('projects'))
    return (
      <main id="main" class="doc-main">
        <ReleaseDocument api={api} id={route.id} cid={route.cid} />
      </main>
    );
  if (route.name === 'agreement-doc' && quotesOn)
    return <main id="main" class="doc-main">{route.part === 'contract' ? <ContractDocument api={api} id={route.id} /> : <AgreementDocument api={api} id={route.id} />}</main>;
  let page;
  switch (route.name) {
    case 'home':
      // the same My work for employees and freelancers (the crew's page follows the role, Home.tsx CrewToday)
      page = <Home api={api} me={me} />;
      break;
    case 'my-time':
      page = timeOn ? <MyTime api={api} week={route.week} /> : blocked(n.timeReporting);
      break;
    case 'availability':
      page = !has('availability') ? blocked(n.availability) : ownAvailability ? <FreelancerAvailability api={api} today={todayStockholm()} /> : me.caps.includes('availability.view') ? <AvailabilityOverview api={api} today={todayStockholm()} canRemind={me.caps.includes('availability.remind')} month={route.month} /> : <NotAvailable title={n.availability} message={t.gate.availabilityEmployees} />;
      break;
    case 'team':
      page = has('team') ? <Team api={api} /> : blocked(n.team);
      break;
    case 'person':
      page = has('team') ? <PersonDetail api={api} personKey={route.key} /> : blocked(n.team);
      break;
    case 'news':
    case 'news-post':
      page = has('news') ? (
        <div class="screen news-page">
          <NewsFeed api={api} lang={lang} postId={route.name === 'news-post' ? route.id : null} open={openNews} edit={(id) => navigate(`/news/${id}/edit`)} write={() => navigate('/news/new')} />
        </div>
      ) : (
        blocked(n.news)
      );
      break;
    case 'news-new':
    case 'news-edit':
      page = !has('news') ? blocked(n.news) : me.caps.includes('news.post') ? <NewsComposer api={api} postId={route.name === 'news-edit' ? route.id : undefined} done={(id) => navigate(id ? `/news/${id}` : '/news')} /> : noAccess(n.writeNews, sellers);
      break;
    case 'projects':
      page = !has('projects') ? blocked(n.projects) : <ProjectsList
              api={api}
              archive={!crew && me.via === 'staff' && me.person.kind === 'employee'}
              crew={crew ? { openAll: !link, open: (r) => navigate(cameraOnly(r.myRoleKeys) ? `/projects/${r.id}/shoots` : `/projects/${r.id}`) } : undefined}
            />;
      break;
    case 'project':
      page = !has('projects') ? (
        blocked(n.projects)
      ) : (
        <ProjectPage key={route.id} api={api} id={route.id} tab={route.tab} crew={link || crew} link={link} meId={me.person.id} />
      );
      break;
    case 'side':
      page = !has('projects') ? blocked(n.projects) : <SideBySide api={api} a={route.a} b={route.b} ta={route.ta} tb={route.tb} mineOnly={link} />;
      break;
    case 'review':
      page = !has('projects') ? (
        blocked(n.projects)
      ) : link ? (
        <CrewPage api={api} review={{ projectId: route.projectId, videoId: route.videoId, version: route.version }} openReview={(o) => navigate(o ? reviewPath(o) : `/projects/${route.projectId}`)} />
      ) : (
        <StaffReview api={api} projectId={route.projectId} videoId={route.videoId} version={route.version} />
      );
      break;
    case 'shoot-prep':
      page = !has('projects') ? blocked(n.projects) : <ShootPrep key={route.shootId} api={api} projectId={route.projectId} shootId={route.shootId} />;
      break;
    case 'talent':
      page = !has('projects') ? blocked(n.actors) : me.caps.includes('projects.edit') && me.via === 'staff' ? <TalentLibrary key={route.tab} api={api} tab={route.tab} /> : noAccess(n.actors, t.gate.producersCd);
      break;
    case 'clients':
      page = has('projects') && !crew ? route.tab === 'contacts' ? <ContactsList api={api} /> : <ClientsList api={api} /> : blocked(n.clients);
      break;
    case 'client':
      page = has('projects') && !crew ? <ClientCard key={route.id} api={api} id={route.id} /> : blocked(n.clients);
      break;
    case 'agreements':
      page = !has('clients') ? blocked(n.quotes) : quotesOn ? <AgreementsList api={api} /> : noAccess(n.quotes, sellers);
      break;
    case 'contracts':
      page = !has('clients') ? blocked(n.contracts) : quotesOn ? <ContractsPage api={api} /> : noAccess(n.contracts, sellers);
      break;
    case 'finance':
      page = financeOn ? <FinancePage key={route.tab} api={api} tab={route.tab} /> : costsOn ? <FinancePage key="costs" api={api} tab="costs" /> : noAccess(n.finance, t.gate.admins);
      break;
    case 'deals':
      page = !has('clients') ? blocked(n.deals) : quotesOn ? <DealsBoard api={api} /> : noAccess(n.deals, sellers);
      break;
    case 'agreement':
      page = !has('clients') ? blocked(n.quotes) : quotesOn ? <AgreementEditor key={route.id ?? 'new'} api={api} id={route.id} /> : noAccess(n.quotes, sellers);
      break;
    case 'agreement-doc':
      page = !has('clients') ? blocked(n.quotes) : noAccess(n.quotes, sellers);
      break;
    case 'agreement-templates':
      page = !has('clients') ? blocked(n.agreementTemplates) : quotesOn ? <AgreementTemplatesPage api={api} /> : noAccess(n.agreementTemplates, sellers);
      break;
    case 'price-list':
      page = !has('clients') ? blocked(n.priceList) : quotesOn ? <PriceListPage api={api} /> : noAccess(n.priceList, sellers);
      break;
    case 'people':
      page = peopleOn ? <PeopleList api={api} /> : me.caps.includes('people.view') ? blocked(n.team) : noAccess(n.people, sellers);
      break;
    case 'people-invite':
      page = admin ? <InvitePerson api={api} domain={WORKSPACE_DOMAIN || 'example.test'} search={location.search} /> : noAccess(n.invitePerson, t.gate.admins);
      break;
    case 'people-person':
      page = admin ? <PersonAdmin api={api} id={route.id} viewAs={canViewAs} /> : noAccess(n.peopleAccess, t.gate.admins);
      break;
    case 'guide':
      page = (
        <div class="screen screen--narrow">
          <GettingStarted api={api} via={link ? 'link' : 'staff'} mode="page" work={guideWork(me.roles)} post={postGuideOn} />
        </div>
      );
      break;
    case 'post':
      page = postOn ? <PostProduction api={api} tab={route.tab} open={route.open} /> : blocked(n.post);
      break;
    case 'archive':
      page = archiveOn ? <ArchivePage api={api} search={location.search} /> : blocked(n.archive);
      break;
    case 'tickets':
      page = has('klara') ? <Tickets api={api} /> : blocked(n.tickets);
      break;
    case 'settings-me':
      page = <MySettings api={api} onLang={me.viewAs ? undefined : p.onLang} calendar={has('projects')} newsPopup={has('news') && !me.viewAs ? me.person.newsPopup !== false : undefined} />;
      break;
    case 'settings-modules':
      page = admin ? <SettingsModules api={api} /> : noAccess(n.settings, t.gate.admins);
      break;
    case 'settings-connections':
      page = admin ? <SettingsModules api={api} part="connections" /> : noAccess(n.settings, t.gate.admins);
      break;
    case 'settings-workload':
      page = admin ? <SettingsWorkload api={api} /> : noAccess(n.settings, t.gate.admins);
      break;
    case 'settings-email':
      page = admin ? <SettingsEmail api={api} /> : noAccess(n.settings, t.gate.admins);
      break;
    case 'settings-whats-new':
      page = <Changelog api={api} />;
      break;
    default:
      page = <NotAvailable title={t.gate.notFound} heading={t.gate.notFoundHead} message={t.gate.notFoundBody} />;
  }
  const tourId = tourOf(route, { crew: crew || link, ownAvailability, financeOn });
  return (
    <Shell name={me.person.name} subtitle={me.roles.join(', ') || (me.person.kind === 'employee' ? t.shell.employee : t.shell.freelancer)} nav={nav} commands={commands} onSignOut={p.signOut} onLang={me.viewAs ? undefined : p.onLang} onViewAs={canViewAs ? () => setViewAsOpen(true) : undefined} menuItems={[...(has('projects') && !me.viewAs ? [{ label: PANEL_TEXT[lang].menu, icon: 'film', onClick: () => setPanelKey(true) }] : []), ...(guideOn ? [{ label: t.shell.guide, icon: 'star', onClick: () => navigate('/guide') }] : [])]} topbar={
        <>
          {timeOn ? <TimerPill api={api} /> : null}
          <Notifications key={p.rev} api={api} corner={me.person.kind === 'employee'} follow />
        </>
      }>
      {p.viewAs && me.viewAs ? <ViewAsBar state={p.viewAs} /> : null}
      <TabsContext.Provider value={tabs}>
        {/* a new key after a language change reloads the page, so texts the server writes follow the choice */}
        <Fragment key={p.rev}>{page}</Fragment>
      </TabsContext.Provider>
      {has('klara') ? <KlaraLauncher api={api} lang={lang} name={me.person.name} /> : null}
      {/* "Planera tid" opens from the week calendar, the project list and the project page (owner 2026-11-03) */}
      {timeOn && !me.viewAs ? <PlanHost api={api} /> : null}
      {/* freelancers make the key from their link (the same panel, owner 2026-10-21: Kom igång tells them to) */}
      {panelKey ? <PanelKeyDialog api={api} path={link ? '/api/crew/panel-token' : undefined} onClose={() => setPanelKey(false)} /> : null}
      {/* what happened since last time: on for everyone unless turned off in My settings (owner 2026-10-21), not when the Hub was opened straight onto News */}
      {viewAsOpen ? <ViewAsDialog api={api} onClose={() => setViewAsOpen(false)} /> : null}
      {/* the page's guided tour, the first time and from Klara; before the popup below, which waits for it */}
      <PageTour key={tourId ?? 'none'} id={tourId} api={api} off={!!me.viewAs} />
      {startNews && has('news') && me.person.newsPopup !== false && !me.viewAs ? <WhatsNew api={api} open={(id) => navigate(id ? `/news/${id}` : '/news')} go={navigate} /> : null}
      <Toaster />
    </Shell>
  );
}

/** Map a failed /api/me to what the sign-in page should say. */
export function loginNotice(e: unknown, token: string): LoginNotice {
  const code = e instanceof ApiError ? e.code : '';
  if (code === 'wrong_domain' || code === 'google_required') return { kind: 'wrong_account', email: tokenEmail(token) };
  if (code === 'not_invited') return { kind: 'not_invited' };
  if (code === 'invalid_token' || code === 'unauthenticated' || code === 'mfa_required') return { kind: 'signed_out' };
  if (!(e instanceof ApiError) || code === 'error') return { kind: 'offline' };
  return { kind: 'error', message: e.message || undefined };
}

export function App() {
  const route = useRoute();
  const [session, setSession] = useState(() => loadSession());
  const [me, setMe] = useState<Me | null>(null);
  const [lang, setLang] = useState<Lang>(storedLang);
  const [rev, setRev] = useState(0);
  const [notice, setNotice] = useState<LoginNotice | null>(() => (sessionExpired() ? { kind: 'signed_out' } : null));
  // read once: the "Continue" page takes the token out of the address bar at once
  const [crewButton] = useState(() => route.name === 'crew-signin' && /[#&]t=/.test(location.hash));
  useEffect(() => {
    if (route.name === 'portal-link') void import('./pages/Portal.js').then((m) => m.keepPortalToken(route.token));
  }, [route.name]);
  // a freelancer's link (/t/{token}, also where the email code lands): kept on the device, then the Hub's own addresses
  const [link, setLink] = useState<string | null>(() => (route.name === 'link' ? route.token : loadLink()));
  const linkToken = route.name === 'link' ? route.token : '';
  useEffect(() => {
    if (!linkToken) return;
    saveLink(linkToken);
    setLink(linkToken);
    history.replaceState(null, '', LINK_TAB_PATH[location.hash.slice(1)] ?? '/');
    dispatchEvent(new PopStateEvent('popstate'));
  }, [linkToken]);
  useEffect(() => {
    if (route.name === 'callback') {
      const s = parseCallback(location.hash);
      saveSession(s);
      setSession(s);
      if (!s) setNotice({ kind: 'cancelled' });
      history.replaceState(null, '', s ? takeReturnTo() : '/');
      dispatchEvent(new PopStateEvent('popstate'));
    }
  }, [route.name]);
  // "Visa som": this tab's view token rides along with the admin's own sign-in until it is stopped or runs out
  const [viewAs] = useState(loadViewAs);
  // the token is renewed before it runs out (auth.ts), so a signed-in tab stays signed in
  const api = useMemo(() => (session ? createApi({ kind: 'staff', token: session.accessToken, fresh: () => freshToken(session), ...(viewAs ? { viewAs: viewAs.token } : {}) }) : null), [session]);
  useEffect(() => {
    if (!api || route.name === 'set-password' || route.name === 'link' || route.name === 'portal' || route.name === 'portal-review' || route.name === 'portal-link' || route.name === 'portal-signin' || route.name === 'crew-signin') return;
    api.get<Me>('/api/me').then((m) => {
      setMe(m);
      preloadAll(STAFF_PAGES);
      if (!m.viewAs) keepEmergency(m.emergencyCopies);
      // the person's saved choice wins over this device's memory (a new laptop, a cleared browser); a freelancer on a
      // password follows the personal link's language (Swedish unless switched)
      const l = m.via === 'link' ? linkLang() : m.person.language === 'sv' ? 'sv' : 'en';
      if (m.via === 'link') document.documentElement.lang = l;
      // looking as someone shows their language without changing this device's choice
      if (!m.viewAs && m.via !== 'link') rememberLang(l);
      setLang(l);
    }, (e) => {
      // a view that ended (30 minutes, or the person changed) goes back to yourself, still signed in
      if (viewAs && e instanceof ApiError && (e.code.startsWith('view_as') || e.code === 'forbidden')) {
        clearViewAs();
        location.assign(viewAs.back);
        return;
      }
      saveSession(null);
      setSession(null);
      setNotice(loginNotice(e, session?.accessToken ?? ''));
    });
  }, [api]);
  if (route.name === 'link') return <Loading />;
  if (route.name === 'set-password') return <SetPasswordPage lang={lang} />;
  // a freelancer's email button (/t/signin#t=…) has its own "Continue" page; without it the one sign-in page shows
  if (route.name === 'crew-signin' && crewButton) return <PortalSignin crew />;
  if (route.name === 'legal') return <LegalPage doc={route.doc} />;
  if (route.name === 'sign') return <SignPage token={route.token} />;
  if (route.name === 'esign') return <ESignPage token={route.token} />;
  if (route.name === 'esign-verify') return <ESignVerifyPage />;
  if (route.name === 'apply') return <ApplyPage ad={route.ad} />;
  if (route.name === 'casting-answer') return <CastingAnswerPage token={route.token} />;
  // the portal keeps its own address; without a session it shows the one sign-in page too
  if (route.name === 'portal-link') return <Loading />;
  if (route.name === 'portal') return <Portal code={route.code} />;
  if (route.name === 'portal-review') return <Portal code={null} review={route.code} />;
  if (route.name === 'portal-signin') return <PortalSignin />;
  if ((!session || !api) && link)
    return (
      <LinkApp
        key={link}
        token={link}
        route={route}
        signOut={() => {
          saveLink(null);
          setLink(null);
          location.assign('/t/signin');
        }}
      />
    );
  if (!session || !api)
    return (
      <LangContext.Provider value={lang}>
        <Login notice={notice} onLang={setLang} />
      </LangContext.Provider>
    );
  if (!me) return <Loading label={DICT[lang].common.loading} />;
  const signOut = () => {
    clearViewAs();
    saveSession(null);
    setMe(null);
    setSession(null);
    navigate('/');
  };
  const pickLang = (l: Lang) => {
    if (me.via === 'link') {
      // a freelancer: kept on the device like on the personal link (the API answers in the page's language)
      try {
        localStorage.setItem(LINK_LANG, l);
      } catch {
        /* private mode: the switch lasts until the page closes */
      }
      document.documentElement.lang = l;
      setLang(l);
      setRev((r) => r + 1);
      return;
    }
    rememberLang(l);
    setLang(l);
    api.put('/api/me/language', { language: l }).then(
      () => setRev((r) => r + 1),
      () => toast({ text: l === 'sv' ? 'Språket kunde inte sparas – det gäller bara den här enheten.' : "Couldn't save the language – it applies to this device only." })
    );
  };
  return (
    <LangContext.Provider value={lang}>
      <StaffApp route={route} api={api} me={me} signOut={signOut} onLang={pickLang} rev={rev} viewAs={viewAs} />
    </LangContext.Provider>
  );
}

/** The guided tour for a page (tours.ts), by role where the page differs: editing and camera see their own pages. */
export function tourOf(route: Route, o: { crew: boolean; ownAvailability: boolean; financeOn: boolean }): TourId | null {
  switch (route.name) {
    case 'home':
      return o.crew ? 'home-crew' : 'home';
    case 'projects':
      return o.crew ? 'projects-crew' : 'projects';
    case 'project':
      return o.crew ? 'project-crew' : 'project';
    case 'review':
      return o.crew ? 'review-crew' : 'review';
    case 'team':
      return 'team';
    case 'availability':
      return o.ownAvailability ? 'availability-own' : 'availability';
    case 'my-time':
      return 'my-time';
    case 'post':
      return 'post-tips';
    case 'clients':
      return route.tab === 'companies' ? 'clients' : null;
    case 'deals':
      return 'deals';
    case 'contracts':
      return 'contracts';
    case 'finance': {
      const tab = o.financeOn ? route.tab : 'costs';
      return tab === 'overview' ? 'finance' : tab === 'invoicing' ? 'finance-invoicing' : tab === 'costs' ? 'finance-costs' : null;
    }
    default:
      return null;
  }
}
